/** Section 3.6 - the modifier contract. Every number that stacks goes through here (D9). */

import type { CreatureDefId, ModifierSrcId } from "./ids.js";
import { FP_ONE, mulFp, roundHalfAway } from "./numeric.js";

/**
 * The stats a `Modifier` may name.
 *
 * Most are a fixed word. Four families are not, because the rule they express
 * is *per entity* and the entity is content: an artifact that makes an army
 * immune to Curse has to name Curse, and a tome that teaches a school has to
 * name the school. Those arms are template literals rather than an enumeration
 * so that content can name a def the union has never heard of, and the
 * authoring schema checks their shape (`immune.<spell>` and so on) rather than
 * their membership.
 */
export type StatKey =
  | "attack" | "defense" | "spellPower" | "knowledge"
  | "speed" | "movement" | "movementSea" | "morale" | "luck"
  | "hp" | "damageMin" | "damageMax" | "shots" | "mana" | "manaRegen"
  | "growth" | "income.gold" | "income.wood" | "income.ore"
  | "income.mercury" | "income.sulfur" | "income.crystal" | "income.gems"
  | `growth.tier${1 | 2 | 3 | 4 | 5 | 6 | 7}`
  | "spellDamage" | "spellDuration" | "spellCost"
  | "scouting" | "experienceGain" | "necromancyPct" | "defenseSiege"
  /**
   * Extra integrity on every wall, gate and tower this town raises - The expansion gives
   * Frostmark +1. Distinct from `defenseSiege`, which thickens the DEFENDER and
   * leaves the masonry exactly as brittle: this one is the masonry.
   */
  | "wallIntegrity"
  /** Extra spells a mage guild holds at every level it has - the Library. */
  | "guildSpells"
  // ---- adventure movement, as a rule rather than a pool
  /** Non-zero: the hero crosses anything, as though permanently under Fly. */
  | "moveFly"
  /** Non-zero: the hero crosses water on foot. */
  | "moveWaterWalk"
  /** Non-zero: river overlay cells cost as a dirt road. */
  | "riverAsRoad"
  /** A floor under the hero's Pathfinding level, 0..3. */
  | "pathfinding"
  /**
   * Non-zero: a rival's observation does not publish this hero.
   *
   * A stat rather than a flag so that a class passive, an artifact and a future
   * secondary skill can all grant it through the one mechanism everything else
   * uses. The rule that decides when a hidden hero is seen anyway lives in
   * `observe.ts` and depends on the two armies, not on this number - so a
   * second point of stealth buys nothing today, and the room is there.
   */
  | "stealth"
  // ---- the negotiating table
  /** Basis points off what an enemy hero asks to surrender. */
  | "surrenderCut"
  /** Basis points on a wandering army's willingness to join or to flee. */
  | "diplomacy"
  // ---- battle-wide, and pointed at the other side
  | "moraleEnemy" | "luckEnemy"
  /** Percent of hostile casts the army shrugs off, on top of any ability. */
  | "magicResist"
  /**
   * Basis points of a hostile cast that rebound on the caster, army-wide.
   *
   * The rule is `battle/spells.ts`'s, written for the Spell Mirror spell and
   * read per stack; this key is how a hero's kit reaches it for the whole army
   * instead. `resolveStat` clamps to `BP_ONE` at the read, so stacking a
   * pendant with the spell cannot send back more than was cast.
   */
  | "magicMirror"
  /** Fixed point taken off the shooter's distance penalty; FP_ONE removes it. */
  | "rangePenaltyCut"
  /** Highest spell level EITHER hero may cast, 0 for no ceiling. */
  | "spellLevelCap"
  /** Lowest spell level either hero may cast, 0 for no floor. */
  | "spellLevelFloor"
  /** Non-zero: neither hero may retreat or surrender. */
  | "noEscape"
  /**
   * Non-zero: creature magic immunity stops applying, to BOTH sides.
   *
   * A creature ability, a pendant's `immune.<spell>` and Null Ward all stop
   * holding. War machines are deliberately left immune - see `spellImmune`,
   * where a destroyed catapult would leave a siege with no way in.
   */
  | "ignoreImmunity"
  /**
   * Non-zero: the dead of this battle cannot be brought back, by either side.
   *
   * Covers both halves - the resurrection spells during the fight, and
   * Gravecalling afterwards - because an artifact that only stopped one would be
   * a rule the player has to memorise rather than a sentence they can read.
   */
  | "noRaise"
  /** Basis points of learning a spell cast against the hero - Spellcatcher. */
  | "eagleEye"
  // ---- army-wide combat, translated by `battleKit` into the battle's own keys
  /**
   * Fixed-point deltas on the damage bracket of every stack in the hero's army:
   * `damageMelee` and `damageRanged` by attack kind, `damageDealt` whatever the
   * kind, `damageTaken` as a share of incoming damage removed.
   *
   * These are Offense, Archery and Armorer as a *number* rather than as a skill
   * level. The skills themselves still travel as `offenseFp` and friends, read
   * off their own rung by `skillDamageFp`; a subskill has no rung of its own to
   * be read from, so it names the effect directly and lands on the same three
   * battle stats through `battleKit`.
   */
  | "damageMelee" | "damageRanged" | "damageDealt" | "damageTaken"
  /** Percent of a melee blow every stack in the army throws back at its attacker. */
  | "fireShield"
  /** Extra retaliations a round, army-wide. */
  | "extraRetaliation"
  /** Hit points every stack mends at the start of its turn. */
  | "regen"
  // ---- floors and widths over a skill the kernel reads as a level
  /** Extra hexes of tactics setup, added to what the Tactics skill bought. */
  | "tacticsHexes"
  /**
   * Floors under the three war-machine skills and the rune cap, 0..3.
   *
   * The same shape as `pathfinding`: the rule reads a level, and a hero's kit
   * may say "at least this good" without the hero holding the skill higher.
   */
  | "artillery" | "ballistics" | "firstAid"
  /** Extra rune levels a stack may bank, over the cap the Runes skill sets. */
  | "runeCap"
  /** Fixed-point multiplier this hero puts on the OTHER hero's spell power. */
  | "spellPowerEnemy"
  // ---- the per-entity families
  /** `immune.<spellId>`: every stack in the army shrugs that spell off. */
  | `immune.${string}`
  /** `schoolDamage.<school>`: fixed-point multiplier on one school's damage. */
  | `schoolDamage.${string}`
  /** `mastery.<school>`: a floor under the hero's mastery in one school. */
  | `mastery.${string}`
  /** `sealSchoolEnemy.<school>`: the ENEMY hero may not cast from that school. */
  | `sealSchoolEnemy.${string}`
  /**
   * `raisesCreature.<defId>`: what this hero's Gravecalling brings home.
   *
   * Without it, `necromancyCreature` picks the cheapest undead of the hero's
   * own faction, which is right for a Bonecaller and wrong for anything that
   * raises something else - The expansion's Conch of the Deep raises Maw Tendrils, who
   * belong to no town. A template family rather than a flag so content names
   * the creature and no rule ever does.
   */
  | `raisesCreature.${string}`
  /** `teachSchool.<school>` / `teachLevel.<n>`: spells the artifact lends. */
  | `teachSchool.${string}`
  | `teachLevel.${string}`;

export type ModifierOp = "add" | "mulFp" | "set" | "min" | "max";

export type ModifierExpiry =
  | { readonly kind: "turns"; readonly turns: number }
  | { readonly kind: "battle" }
  | { readonly kind: "round"; readonly rounds: number }
  | { readonly kind: "permanent" };

export interface Modifier {
  readonly source: ModifierSrcId;
  readonly stat: StatKey;
  readonly op: ModifierOp;
  /** Integer. For `mulFp`, fixed point at FP_ONE: 1250 means times 1.25. */
  readonly value: number;
  readonly stackGroup?: string;
  readonly stackRule?: "highest" | "sum" | "first";
  readonly expires?: ModifierExpiry;
}

/**
 * Modifiers that apply to one creature def rather than to everything a hero
 * touches. `Modifier` itself has no scope field on purpose - a scope that every
 * consumer had to remember to honour would be a scope half the consumers got
 * wrong - so a scoped bonus is carried as a pair and unpacked by the one place
 * that knows which stack is which: `battle/init.ts`, when it builds the store.
 */
export interface CreatureModifiers {
  readonly def: CreatureDefId;
  readonly mods: readonly Modifier[];
}

export interface ModifierTerm {
  readonly source: ModifierSrcId;
  readonly op: ModifierOp;
  readonly value: number;
  readonly applied: boolean;
  /** Set when `applied` is false. */
  readonly suppressedBy?: ModifierSrcId;
}

export interface Explained<T> {
  readonly value: T;
  readonly base: number;
  readonly terms: readonly ModifierTerm[];
}

const OP_ORDER: Readonly<Record<ModifierOp, number>> = {
  set: 0, add: 1, mulFp: 2, min: 3, max: 3,
};

/**
 * Fixed resolution order: `set`, then `add`, then `mulFp` (all multiplied
 * together in fixed point and rounded exactly once), then `min` / `max`.
 *
 * Within a stack group the `stackRule` decides which members survive; the rest
 * are reported as terms with `applied: false` so a tooltip can say why.
 *
 * Modifiers are sorted by source before resolution (I3), so two states that
 * differ only in insertion order resolve identically.
 */
export function resolveStat(base: number, mods: readonly Modifier[]): Explained<number> {
  const sorted = [...mods].sort((a, b) =>
    a.source < b.source ? -1 : a.source > b.source ? 1
      : OP_ORDER[a.op] - OP_ORDER[b.op] || a.value - b.value);

  const suppressed = new Map<Modifier, ModifierSrcId>();
  const groups = new Map<string, Modifier[]>();
  for (const m of sorted) {
    if (m.stackGroup === undefined) continue;
    const key = `${m.stackGroup} ${m.stat} ${m.op}`;
    const g = groups.get(key);
    if (g) g.push(m); else groups.set(key, [m]);
  }
  for (const [, g] of [...groups.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
    if (g.length < 2) continue;
    const rule = g[0]!.stackRule ?? "highest";
    if (rule === "sum") continue;
    let keep = g[0]!;
    if (rule === "highest") for (const m of g) if (m.value > keep.value) keep = m;
    for (const m of g) if (m !== keep) suppressed.set(m, keep.source);
  }

  const terms: ModifierTerm[] = [];
  let setValue: number | null = null;
  let addTotal = 0;
  let mulAcc = FP_ONE;
  const mins: number[] = [];
  const maxes: number[] = [];

  for (const m of sorted) {
    const sup = suppressed.get(m);
    if (sup !== undefined) {
      terms.push({ source: m.source, op: m.op, value: m.value, applied: false, suppressedBy: sup });
      continue;
    }
    terms.push({ source: m.source, op: m.op, value: m.value, applied: true });
    switch (m.op) {
      case "set": setValue = m.value; break;
      case "add": addTotal += m.value; break;
      case "mulFp": mulAcc = roundHalfAway((mulAcc * m.value) / FP_ONE); break;
      case "min": mins.push(m.value); break;
      case "max": maxes.push(m.value); break;
    }
  }

  let v = (setValue ?? base) + addTotal;
  if (mulAcc !== FP_ONE) v = mulFp(v, mulAcc);
  for (const lo of maxes) if (v < lo) v = lo;   // "max" raises the floor
  for (const hi of mins) if (v > hi) v = hi;    // "min" caps the ceiling

  return { value: v, base, terms };
}

/** Tick down turn- or round-scoped modifiers; returns those still alive. */
export function expireModifiers(
  mods: readonly Modifier[], kind: "turns" | "rounds",
): readonly Modifier[] {
  const out: Modifier[] = [];
  for (const m of mods) {
    const e = m.expires;
    if (e === undefined || e.kind === "permanent" || e.kind === "battle") { out.push(m); continue; }
    if (kind === "turns" && e.kind === "turns") {
      if (e.turns > 1) out.push({ ...m, expires: { kind: "turns", turns: e.turns - 1 } });
      continue;
    }
    if (kind === "rounds" && e.kind === "round") {
      if (e.rounds > 1) out.push({ ...m, expires: { kind: "round", rounds: e.rounds - 1 } });
      continue;
    }
    out.push(m);
  }
  return out;
}

/** Drop everything scoped to a battle that just ended. */
export function dropBattleModifiers(mods: readonly Modifier[]): readonly Modifier[] {
  return mods.filter((m) => m.expires?.kind !== "battle" && m.expires?.kind !== "round");
}
